import React, { useEffect, useState, useMemo, useRef } from "react";
import {
	Box,
	Typography,
	Card,
	IconButton,
	Button,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	LinearProgress,
	Collapse,
	Snackbar,
	Alert,
	Menu,
	Chip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AppLayout from "../../components/AppLayout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AddIcon from "@mui/icons-material/Add";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DownloadIcon from "@mui/icons-material/Download";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
import PeopleIcon from "@mui/icons-material/People";
import ColetaService from "../../service/coleta_service";
import DemandaService from "../../service/demanda_service";
import FornecedorService from "../../service/fornecedor_service";
import ProdutoService from "../../service/produto_service";
import UnidadeMedidaService from "../../service/unidade_medida_service";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toLocalISODate } from "../../utils/dateUtils";

function formatCultivo(cultivo) {
	if (!cultivo) return "Produto";
	const cultura = cultivo.cultura || "Produto";
	const variedade = cultivo.variedade?.trim();
	return variedade ? `${cultura} ${variedade}` : cultura;
}

// "1.250,5": quantidade como o distribuidor le, sem casas a mais.
function formatarNumero(valor) {
	return Number(valor || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 });
}

// Pedido do WhatsApp que a coleta responde (nome do agendamento). Envio direto nao
// tem nome: aparece como "Pedido via WhatsApp". Coleta manual nao tem pedido.
function nomePedido(coleta) {
	if (!coleta?.lembrete) return "";
	return coleta.lembrete.config?.nome?.trim() || "Pedido via WhatsApp";
}

function Coletas() {
	const navigate = useNavigate();
	const [selectedDate, setSelectedDate] = useState(new Date());
	const [loading, setLoading] = useState(false);
	const [coberturas, setCoberturas] = useState([]);
	const [coberturaGeral, setCoberturaGeral] = useState(0);
	// Produtos em que o confirmado passou da demanda do dia (ou que nem tem demanda):
	// a barra de cobertura para em 100%, entao o excesso so aparece neste aviso.
	const [excedentes, setExcedentes] = useState([]);
	const [coletasAgrupadas, setColetasAgrupadas] = useState([]);
	const [expandedProduct, setExpandedProduct] = useState(null);
	const [expandedSupplier, setExpandedSupplier] = useState(null);

	// Dialog states
	const [demandaDialogOpen, setDemandaDialogOpen] = useState(false);
	const [coletaDialogOpen, setColetaDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [editingDemanda, setEditingDemanda] = useState(null);
	const [editingColeta, setEditingColeta] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);

	// Form states
	const [formProduto, setFormProduto] = useState("");
	const [formQuantidade, setFormQuantidade] = useState("");
	const [formUnidadeMedida, setFormUnidadeMedida] = useState("");
	const [formFornecedor, setFormFornecedor] = useState("");
	const [formDataPrevista, setFormDataPrevista] = useState("");

	// Data lists
	const [produtos, setProdutos] = useState([]);
	const [fornecedores, setFornecedores] = useState([]);
	const [unidadesMedida, setUnidadesMedida] = useState([]);

	// Snackbar
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

	// Export menu
	const [exportAnchor, setExportAnchor] = useState(null);

	// Retificações que os fornecedores fizeram pelo WhatsApp e a unidade ainda não viu.
	// Valem para qualquer dia, não só o selecionado: o aviso é justamente para quem não
	// está olhando aquele dia.
	const [retificacoes, setRetificacoes] = useState([]);
	const [verTodasRetificacoes, setVerTodasRetificacoes] = useState(false);

	const formatDate = (date) => {
		const options = { weekday: "long", day: "numeric", month: "long" };
		return date.toLocaleDateString("pt-BR", options);
	};

	const goToPrevDay = () => {
		const newDate = new Date(selectedDate);
		newDate.setDate(newDate.getDate() - 1);
		setSelectedDate(newDate);
	};

	const goToNextDay = () => {
		const newDate = new Date(selectedDate);
		newDate.setDate(newDate.getDate() + 1);
		setSelectedDate(newDate);
	};

	const dateInputRef = useRef(null);

	const openDatePicker = () => {
		const input = dateInputRef.current;
		if (!input) return;
		if (typeof input.showPicker === "function") {
			input.showPicker();
		} else {
			input.focus();
			input.click();
		}
	};

	const handleDateInputChange = (e) => {
		const value = e.target.value;
		if (!value) return;
		const [y, m, d] = value.split("-").map(Number);
		setSelectedDate(new Date(y, m - 1, d));
	};

	useEffect(() => {
		loadData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedDate]);

	useEffect(() => {
		loadLists();
	}, []);

	const loadLists = async () => {
		try {
			const unidadeId = localStorage.getItem("selected_unidade_id");
			if (!unidadeId) return;

			const fornecedorService = new FornecedorService();
			const produtoService = new ProdutoService();
			const unidadeMedidaService = new UnidadeMedidaService();

			const [fornecedoresRes, produtosRes, unidadesRes] = await Promise.all([
				fornecedorService.getByUnidade(unidadeId).catch((e) => { console.error("Erro fornecedores:", e); return []; }),
				produtoService.getByUnidade(unidadeId, "entrada").catch((e) => { console.error("Erro produtos:", e); return []; }),
				unidadeMedidaService.getAllByCultivo().catch((e) => { console.error("Erro unidades:", e); return []; }),
			]);

			console.log("=== DEBUG COLETAS ===");
			console.log("unidadeId:", unidadeId);
			console.log("produtosRes:", produtosRes);

			// Os repositórios retornam arrays diretamente ou objetos com .data
			const fornecedoresData = Array.isArray(fornecedoresRes) ? fornecedoresRes : (fornecedoresRes.data || []);
			const produtosData = Array.isArray(produtosRes) ? produtosRes : (produtosRes.data || []);
			const unidadesData = Array.isArray(unidadesRes) ? unidadesRes : (unidadesRes.data || []);

			console.log("produtosData final:", produtosData);

			setFornecedores(fornecedoresData);
			setProdutos(produtosData);
			setUnidadesMedida(unidadesData);
		} catch (error) {
			console.error("Erro ao carregar listas:", error);
		}
	};

	const loadData = async () => {
		try {
			setLoading(true);
			const unidadeId = localStorage.getItem("selected_unidade_id");
			if (!unidadeId) return;

			const demandaService = new DemandaService();
			const coletaService = new ColetaService();
			const dateStr = toLocalISODate(selectedDate);

			const [coberturaRes, agrupadoRes] = await Promise.all([
				demandaService.getCobertura(unidadeId, dateStr).catch(() => ({ data: { coberturas: [], coberturaGeral: 0 } })),
				coletaService.getColetasAgrupadasPorFornecedor(unidadeId, dateStr).catch(() => ({ data: [] })),
			]);

			setCoberturas(coberturaRes.data?.coberturas || []);
			setCoberturaGeral(coberturaRes.data?.coberturaGeral || 0);
			setExcedentes(coberturaRes.data?.excedentes || []);
			setColetasAgrupadas(agrupadoRes.data || []);
			loadRetificacoes();
		} catch (error) {
			console.error("Erro ao carregar dados:", error);
		} finally {
			setLoading(false);
		}
	};

	const loadRetificacoes = async () => {
		const unidadeId = localStorage.getItem("selected_unidade_id");
		if (!unidadeId) return;
		try {
			setRetificacoes(await new ColetaService().getRetificacoesNaoVistas(unidadeId));
		} catch (error) {
			console.error("Erro ao carregar retificações:", error);
		}
	};

	// Sem ids, marca todas. O service avisa o menu para o badge acompanhar.
	const marcarRetificacoesVistas = async (ids) => {
		const unidadeId = localStorage.getItem("selected_unidade_id");
		if (!unidadeId) return;
		try {
			await new ColetaService().marcarRetificacoesVistas(unidadeId, ids);
			setRetificacoes((atuais) => (ids ? atuais.filter((r) => !ids.includes(r.id)) : []));
		} catch (error) {
			console.error("Erro ao marcar retificações:", error);
			setSnackbar({ open: true, message: "Erro ao marcar retificações como vistas", severity: "error" });
		}
	};

	const irParaRetificacao = (r) => {
		const d = new Date(r.dataColeta);
		setSelectedDate(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
		if (r.fornecedorId) setExpandedSupplier(r.fornecedorId);
	};

	// Coletas com retificação ainda não vista: ganham a marca "Retificada" na lista.
	const coletasRetificadas = useMemo(() => new Set(retificacoes.map((r) => r.coletaId)), [retificacoes]);

	const descreverRetificacao = (r) =>
		r.tipo === "CANCELADA"
			? `cancelou ${r.produto} (era ${r.quantidadeAnterior})`
			: `alterou ${r.produto}: ${r.quantidadeAnterior} → ${r.quantidadeNova}`;

	const quandoFoi = (iso) => {
		const d = new Date(iso);
		return `${d.toLocaleDateString("pt-BR")} às ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
	};

	const resetForm = () => {
		setFormProduto("");
		setFormQuantidade("");
		setFormUnidadeMedida("");
		setFormFornecedor("");
		setFormDataPrevista("");
		setEditingDemanda(null);
		setEditingColeta(null);
	};

	const openAddDemanda = () => {
		resetForm();
		setDemandaDialogOpen(true);
	};

	const openEditDemanda = (demanda) => {
		setEditingDemanda(demanda);
		setFormProduto(demanda.produtoId || "");
		setFormQuantidade(String(demanda.quantidade));
		setFormUnidadeMedida(demanda.unidade || "");
		setDemandaDialogOpen(true);
	};

	const openAddColeta = () => {
		resetForm();
		setColetaDialogOpen(true);
	};

	const openEditColeta = (coleta) => {
		setEditingColeta(coleta);
		setFormProduto(coleta.produtoUnidadeDistribuidoraId || "");
		setFormQuantidade(String(coleta.quantidade));
		setFormUnidadeMedida(coleta.unidadeMedidaId || "");
		setFormFornecedor(coleta.fornecedorId || "");
		setFormDataPrevista(coleta.dataPrevista ? coleta.dataPrevista.split("T")[0] : "");
		setColetaDialogOpen(true);
	};

	const handleSaveDemanda = async () => {
		try {
			if (!formProduto || !formQuantidade || !formUnidadeMedida) {
				setSnackbar({ open: true, message: "Preencha todos os campos", severity: "error" });
				return;
			}

			const unidadeId = localStorage.getItem("selected_unidade_id");
			const demandaService = new DemandaService();

			const data = {
				id: editingDemanda?.id,
				unidadeDistribuidoraId: unidadeId,
				produtoUnidadeDistribuidoraId: formProduto,
				unidadeMedidaId: formUnidadeMedida,
				data: toLocalISODate(selectedDate),
				quantidade: parseFloat(formQuantidade),
			};

			if (editingDemanda?.id) {
				await demandaService.update(data);
				setSnackbar({ open: true, message: "Demanda atualizada!", severity: "success" });
			} else {
				await demandaService.save(data);
				setSnackbar({ open: true, message: "Demanda cadastrada!", severity: "success" });
			}

			setDemandaDialogOpen(false);
			resetForm();
			loadData();
		} catch (error) {
			console.error("Erro ao salvar demanda:", error);
			setSnackbar({ open: true, message: "Erro ao salvar demanda", severity: "error" });
		}
	};

	const handleSaveColeta = async () => {
		try {
			if (!formProduto || !formQuantidade || !formUnidadeMedida) {
				setSnackbar({ open: true, message: "Preencha todos os campos obrigatórios", severity: "error" });
				return;
			}

			const unidadeId = localStorage.getItem("selected_unidade_id");
			const coletaService = new ColetaService();

			const data = {
				id: editingColeta?.id,
				unidadeDistribuidoraId: unidadeId,
				fornecedorId: formFornecedor || null,
				produtoUnidadeDistribuidoraId: formProduto,
				unidadeMedidaId: formUnidadeMedida,
				data: toLocalISODate(selectedDate),
				quantidade: parseFloat(formQuantidade),
				dataPrevista: formDataPrevista || null,
				status: editingColeta?.status || "PENDENTE",
			};

			if (editingColeta?.id) {
				await coletaService.update(data);
				setSnackbar({ open: true, message: "Coleta atualizada!", severity: "success" });
			} else {
				await coletaService.save(data);
				setSnackbar({ open: true, message: "Coleta registrada!", severity: "success" });
			}

			setColetaDialogOpen(false);
			resetForm();
			loadData();
		} catch (error) {
			console.error("Erro ao salvar coleta:", error);
			setSnackbar({ open: true, message: "Erro ao salvar coleta", severity: "error" });
		}
	};

	const handleDelete = async () => {
		try {
			if (!deleteTarget) return;

			if (deleteTarget.type === "demanda") {
				const demandaService = new DemandaService();
				await demandaService.changeVisibility(deleteTarget.id);
				setSnackbar({ open: true, message: "Demanda removida!", severity: "success" });
			} else {
				const coletaService = new ColetaService();
				await coletaService.changeVisibility(deleteTarget.id);
				setSnackbar({ open: true, message: "Coleta removida!", severity: "success" });
			}

			setDeleteDialogOpen(false);
			setDeleteTarget(null);
			loadData();
		} catch (error) {
			console.error("Erro ao excluir:", error);
			setSnackbar({ open: true, message: "Erro ao excluir", severity: "error" });
		}
	};

	const handleConfirmarColeta = async (id) => {
		try {
			const coletaService = new ColetaService();
			await coletaService.confirmar(id);
			setSnackbar({ open: true, message: "Coleta confirmada!", severity: "success" });
			loadData();
		} catch (error) {
			console.error("Erro ao confirmar:", error);
			setSnackbar({ open: true, message: "Erro ao confirmar coleta", severity: "error" });
		}
	};

	const getCoverageColor = (pct) => {
		if (pct >= 80) return "#1a9e8f";
		if (pct >= 50) return "#f5a623";
		return "#e74c3c";
	};

	const totalColetas = useMemo(() => {
		return coletasAgrupadas.reduce((sum, g) => sum + g.coletas.length, 0);
	}, [coletasAgrupadas]);

	const formatDateBR = (dateStr) => {
		if (!dateStr) return "";
		const d = new Date(dateStr);
		return d.toLocaleDateString("pt-BR");
	};

	const exportCSV = () => {
		const rows = [["Fornecedor", "Produto", "Quantidade", "Unidade", "Data Prevista", "Status"]];
		coletasAgrupadas.forEach((g) => {
			g.coletas.forEach((c) => {
				rows.push([
					g.fornecedorNome,
					formatCultivo(c.produtoUnidadeDistribuidora?.cultivo) + (nomePedido(c) ? ` (${nomePedido(c)})` : ""),
					c.quantidade,
					c.unidadeMedida?.sigla || "",
					formatDateBR(c.dataPrevista),
					c.status,
				]);
			});
		});
		const csvContent = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
		const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `coletas_${toLocalISODate(selectedDate)}.csv`;
		link.click();
		URL.revokeObjectURL(url);
		setExportAnchor(null);
		setSnackbar({ open: true, message: "CSV exportado!", severity: "success" });
	};

	const exportPDF = () => {
		const doc = new jsPDF();
		doc.setFontSize(16);
		doc.text("Sisagri — Coletas", 14, 20);
		doc.setFontSize(11);
		const dateLabel = formatDate(selectedDate);
		doc.text(dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1), 14, 28);
		doc.setFontSize(9);
		doc.text(`Gerado em: ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`, 14, 34);

		if (coberturas.length > 0) {
			doc.setFontSize(12);
			doc.text("Cobertura da Demanda", 14, 44);
			autoTable(doc, {
				startY: 48,
				head: [["Produto", "Demanda", "Confirmado", "Cobertura"]],
				body: coberturas.map((c) => [c.produto, `${c.quantidade} ${c.unidade}`, `${c.previsto} ${c.unidade}`, `${c.percentual}%`]),
				styles: { fontSize: 9 },
				headStyles: { fillColor: [26, 158, 143] },
			});
		}

		const startY = coberturas.length > 0 ? (doc.lastAutoTable?.finalY || 60) + 10 : 40;
		doc.setFontSize(12);
		doc.text("Previsões por Fornecedor", 14, startY);
		const bodyRows = [];
		coletasAgrupadas.forEach((g) => {
			g.coletas.forEach((c) => {
				bodyRows.push([
					g.fornecedorNome,
					formatCultivo(c.produtoUnidadeDistribuidora?.cultivo) + (nomePedido(c) ? ` (${nomePedido(c)})` : ""),
					c.quantidade,
					c.unidadeMedida?.sigla || "",
					formatDateBR(c.dataPrevista),
					c.status === "CONFIRMADO" ? "Confirmado" : "Pendente",
				]);
			});
		});
		autoTable(doc, {
			startY: startY + 4,
			head: [["Fornecedor", "Produto", "Qtd", "Unidade", "Data Prevista", "Status"]],
			body: bodyRows,
			styles: { fontSize: 9 },
			headStyles: { fillColor: [26, 158, 143] },
		});

		doc.save(`coletas_${toLocalISODate(selectedDate)}.pdf`);
		setExportAnchor(null);
		setSnackbar({ open: true, message: "PDF exportado!", severity: "success" });
	};

	return (
		<AppLayout currentPage="coletas">
			<Box sx={{ maxWidth: 1400, mx: "auto" }}>
					{/* Header */}
					<Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "stretch", sm: "center" }, gap: 2, mb: 3 }}>
						<Box sx={{ display: { xs: "none", md: "block" } }}>
							<Typography variant="h4" sx={{ color: "#0f4557", fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
								Coletas
							</Typography>
							<Typography variant="body2" sx={{ color: "#5a7a88", mt: 0.5 }}>
								Demanda × previsões dos fornecedores por dia
							</Typography>
						</Box>

						<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
							{/* Date Navigation */}
							<Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 1, bgcolor: "white", border: "1px solid #dfe3e8", borderRadius: "12px", px: 2, py: 1 }}>
								<IconButton size="small" onClick={goToPrevDay}>
									<ChevronLeftIcon />
								</IconButton>
								<Typography variant="body2" sx={{ fontWeight: 500, color: "#0f4557", textTransform: "capitalize", minWidth: 180, textAlign: "center" }}>
									{formatDate(selectedDate)}
								</Typography>
								<IconButton size="small" onClick={goToNextDay}>
									<ChevronRightIcon />
								</IconButton>
								<IconButton size="small" onClick={openDatePicker}>
									<CalendarTodayIcon fontSize="small" />
								</IconButton>
								<input
									ref={dateInputRef}
									type="date"
									value={toLocalISODate(selectedDate)}
									onChange={handleDateInputChange}
									style={{ position: "absolute", bottom: 0, right: 8, width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
									tabIndex={-1}
									aria-hidden="true"
								/>
							</Box>

							<Button
								variant="contained"
								startIcon={<WhatsAppIcon />}
								onClick={() => navigate("/whatsapp")}
								sx={{ bgcolor: "#25D366", "&:hover": { bgcolor: "#20bd5a" }, textTransform: "none" }}
							>
								WhatsApp
							</Button>
							<Button
								variant="outlined"
								startIcon={<DownloadIcon />}
								onClick={(e) => setExportAnchor(e.currentTarget)}
								sx={{ borderColor: "#dfe3e8", color: "#0f4557" }}
							>
								Exportar
							</Button>
							<Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
								<MenuItem onClick={exportCSV}>Exportar CSV</MenuItem>
								<MenuItem onClick={exportPDF}>Exportar PDF</MenuItem>
							</Menu>
						</Box>
					</Box>

					{/* Retificações dos fornecedores ainda não vistas */}
					{retificacoes.length > 0 && (
						<Alert
							severity="warning"
							icon={<NotificationsActiveIcon />}
							sx={{ mb: 3, borderRadius: "16px", border: "1px solid #f5a62355", "& .MuiAlert-message": { width: "100%" } }}
						>
							<Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, gap: 1, mb: 1 }}>
								<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
									{retificacoes.length === 1
										? "1 retificação de fornecedor"
										: `${retificacoes.length} retificações de fornecedores`}
								</Typography>
								<Button size="small" onClick={() => marcarRetificacoesVistas()} sx={{ textTransform: "none", color: "#b26a00" }}>
									Marcar todas como vistas
								</Button>
							</Box>
							{(verTodasRetificacoes ? retificacoes : retificacoes.slice(0, 5)).map((r) => (
								<Box
									key={r.id}
									sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, gap: 1, py: 0.75, borderTop: "1px solid #f5a62333" }}
								>
									<Typography variant="body2" sx={{ color: "#5c3d00" }}>
										<strong>{r.fornecedorNome}</strong> {descreverRetificacao(r)} — coleta de{" "}
										<strong>{formatDateBR(r.dataColeta)}</strong>
										<Typography component="span" variant="caption" sx={{ color: "#8e7a55", ml: 1 }}>
											({quandoFoi(r.criadaEm)})
										</Typography>
									</Typography>
									<Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
										<Button size="small" onClick={() => irParaRetificacao(r)} sx={{ textTransform: "none", color: "#0f4557" }}>
											Ver dia
										</Button>
										<Button size="small" onClick={() => marcarRetificacoesVistas([r.id])} sx={{ textTransform: "none", color: "#b26a00" }}>
											Ok, visto
										</Button>
									</Box>
								</Box>
							))}
							{retificacoes.length > 5 && (
								<Button size="small" onClick={() => setVerTodasRetificacoes((v) => !v)} sx={{ textTransform: "none", color: "#b26a00", mt: 0.5 }}>
									{verTodasRetificacoes ? "Mostrar menos" : `Ver todas (${retificacoes.length})`}
								</Button>
							)}
						</Alert>
					)}

					{loading && <LinearProgress sx={{ mb: 2 }} />}

					{/* Confirmado acima da demanda: o distribuidor vai receber mais do que pediu. */}
					{excedentes.length > 0 && (
						<Alert
							severity="warning"
							icon={<WarningAmberIcon />}
							sx={{ mb: 3, borderRadius: "16px", border: "1px solid #f5a62355", "& .MuiAlert-message": { width: "100%" } }}
						>
							<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
								{excedentes.length === 1
									? "1 produto com quantidade confirmada acima da demanda"
									: `${excedentes.length} produtos com quantidade confirmada acima da demanda`}
							</Typography>
							{excedentes.map((e) => (
								<Typography key={`${e.produto}|${e.unidade}|${e.demandaId || "sem"}`} variant="body2" sx={{ color: "#5c3d00", py: 0.25 }}>
									<strong>{e.produto}</strong>:{" "}
									{e.semDemanda
										? `${formatarNumero(e.confirmado)} ${e.unidade} confirmados, sem demanda cadastrada para o dia`
										: `${formatarNumero(e.confirmado)} ${e.unidade} confirmados para uma demanda de ${formatarNumero(e.demanda)} ${e.unidade} — ${formatarNumero(e.excedente)} ${e.unidade} a mais`}
								</Typography>
							))}
						</Alert>
					)}

					{/* Coverage Section */}
					<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8", mb: 3 }}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2.5, borderBottom: "1px solid #dfe3e8" }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
								<TrackChangesIcon sx={{ color: "#1a9e8f" }} />
								<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557" }}>
									Cobertura da Demanda
								</Typography>
								{coberturaGeral > 0 && (
									<Typography variant="subtitle1" sx={{ fontWeight: 700, color: getCoverageColor(coberturaGeral), ml: 1 }}>
										{coberturaGeral}%
									</Typography>
								)}
							</Box>
							<Button startIcon={<AddIcon />} variant="contained" size="small" onClick={openAddDemanda} sx={{ bgcolor: "#1a9e8f", "&:hover": { bgcolor: "#158f80" } }}>
								Nova Demanda
							</Button>
						</Box>

						{coberturas.length === 0 ? (
							<Box sx={{ p: 4, textAlign: "center" }}>
								<TrackChangesIcon sx={{ fontSize: 48, color: "#dfe3e8", mb: 1 }} />
								<Typography variant="body2" sx={{ color: "#8e9bae" }}>
									Nenhuma demanda cadastrada para este dia
								</Typography>
								<Button size="small" onClick={openAddDemanda} sx={{ mt: 1, color: "#1a9e8f" }}>
									Cadastrar demanda
								</Button>
							</Box>
						) : (
							coberturas.map((item) => {
								const isExpanded = expandedProduct === item.id;
								return (
									<Box key={item.id}>
										<Box
											onClick={() => setExpandedProduct(isExpanded ? null : item.id)}
											sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, cursor: "pointer", "&:hover": { bgcolor: "#f8f9fa" }, borderBottom: "1px solid #f0f2f5" }}
										>
											<Box sx={{ flex: 1 }}>
												<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
													<Typography variant="body2" sx={{ fontWeight: 500, color: "#0f4557" }}>
														{item.produto}
													</Typography>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														{item.excedente > 0 && (
															<Chip
																size="small"
																icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
																label={`+${formatarNumero(item.excedente)} ${item.unidade} acima`}
																sx={{ bgcolor: "#f5a62320", color: "#b26a00", fontWeight: 600, fontSize: "0.7rem", "& .MuiChip-icon": { color: "#b26a00" } }}
															/>
														)}
														<Typography variant="caption" sx={{ color: "#8e9bae" }}>
															{item.previsto}/{item.quantidade} {item.unidade}
														</Typography>
														<Typography variant="body2" sx={{ fontWeight: 700, color: getCoverageColor(item.percentual) }}>
															{item.percentual}%
														</Typography>
													</Box>
												</Box>
												<LinearProgress
													variant="determinate"
													value={item.percentual}
													sx={{
														height: 6,
														borderRadius: 3,
														bgcolor: "#dfe3e8",
														"& .MuiLinearProgress-bar": { borderRadius: 3, bgcolor: getCoverageColor(item.percentual) },
													}}
												/>
											</Box>
											<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
												<Typography variant="caption" sx={{ color: "#8e9bae" }}>
													{item.fornecedores?.length || 0}
												</Typography>
												<PeopleIcon sx={{ fontSize: 14, color: "#8e9bae" }} />
												{isExpanded ? <ExpandLessIcon sx={{ color: "#8e9bae" }} /> : <ExpandMoreIcon sx={{ color: "#8e9bae" }} />}
											</Box>
										</Box>
										<Collapse in={isExpanded}>
											<Box sx={{ bgcolor: "#f8f9fa", p: 2, borderBottom: "1px solid #f0f2f5" }}>
												<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
													<Typography variant="caption" sx={{ fontWeight: 600, color: "#8e9bae", textTransform: "uppercase" }}>
														Fornecedores que contribuem
													</Typography>
													<Box>
														<IconButton size="small" onClick={() => openEditDemanda(item)}>
															<EditIcon fontSize="small" />
														</IconButton>
														<IconButton size="small" onClick={() => { setDeleteTarget({ type: "demanda", id: item.id }); setDeleteDialogOpen(true); }}>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</Box>
												</Box>
												{item.fornecedores?.length === 0 ? (
													<Typography variant="caption" sx={{ color: "#8e9bae", fontStyle: "italic" }}>
														Nenhum fornecedor previsto ainda
													</Typography>
												) : (
													item.fornecedores?.map((f) => (
														<Box key={f.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1 }}>
															<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
																<Box sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: "#1a9e8f15", display: "flex", alignItems: "center", justifyContent: "center" }}>
																	<PersonIcon sx={{ fontSize: 14, color: "#1a9e8f" }} />
																</Box>
																<Typography variant="body2" sx={{ color: "#0f4557" }}>
																	{f.fornecedorNome}
																</Typography>
															</Box>
															<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
																<Typography variant="body2" sx={{ fontWeight: 500, color: "#0f4557" }}>
																	{f.quantidade} {f.unidade}
																</Typography>
																<Box sx={{
																	px: 1,
																	py: 0.25,
																	borderRadius: "12px",
																	bgcolor: f.status === "CONFIRMADO" ? "#27ae6015" : "#f5a62315",
																	color: f.status === "CONFIRMADO" ? "#27ae60" : "#f5a623",
																	fontSize: "0.625rem",
																	fontWeight: 500,
																}}>
																	{f.status === "CONFIRMADO" ? "Confirmado" : "Pendente"}
																</Box>
															</Box>
														</Box>
													))
												)}
												<Box sx={{ display: "flex", justifyContent: "space-between", mt: 1.5, pt: 1.5, borderTop: "1px solid #dfe3e8" }}>
													<Typography variant="caption" sx={{ color: "#8e9bae" }}>
														Confirmado: <strong style={{ color: "#0f4557" }}>{item.confirmado} {item.unidade}</strong>
													</Typography>
													<Typography variant="caption" sx={{ color: "#8e9bae" }}>
														Total previsto: <strong style={{ color: "#0f4557" }}>{item.previsto} {item.unidade}</strong>
													</Typography>
												</Box>
											</Box>
										</Collapse>
									</Box>
								);
							})
						)}
					</Card>

					{/* Collections by Supplier */}
					<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8" }}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2.5, borderBottom: "1px solid #dfe3e8" }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
								<TrendingUpIcon sx={{ color: "#1a9e8f" }} />
								<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557" }}>
									Previsões dos Produtores ({totalColetas})
								</Typography>
							</Box>
							<Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={openAddColeta} sx={{ borderColor: "#1a9e8f", color: "#1a9e8f" }}>
								Nova Coleta
							</Button>
						</Box>

						{coletasAgrupadas.length === 0 ? (
							<Box sx={{ p: 4, textAlign: "center" }}>
								<LocalFloristIcon sx={{ fontSize: 48, color: "#dfe3e8", mb: 1 }} />
								<Typography variant="body2" sx={{ color: "#8e9bae" }}>
									Nenhuma previsão para este dia
								</Typography>
							</Box>
						) : (
							coletasAgrupadas.map((group) => {
								const isExpanded = expandedSupplier === group.fornecedorId;
								// Quem so respondeu "nao vou ter" nao tem coleta para abrir
								const expansivel = group.coletas.length > 0;
								return (
									<Box key={group.fornecedorId}>
										<Box
											onClick={() => expansivel && setExpandedSupplier(isExpanded ? null : group.fornecedorId)}
											sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, cursor: expansivel ? "pointer" : "default", "&:hover": { bgcolor: "#f8f9fa" }, borderBottom: "1px solid #f0f2f5" }}
										>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
												<Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: "#1a9e8f15", display: "flex", alignItems: "center", justifyContent: "center" }}>
													<PersonIcon sx={{ fontSize: 18, color: "#1a9e8f" }} />
												</Box>
												<Box>
													<Typography variant="body2" sx={{ fontWeight: 600, color: "#0f4557" }}>
														{group.fornecedorNome}
													</Typography>
													<Typography variant="caption" sx={{ color: "#8e9bae" }}>
														{expansivel
															? `${group.totalProdutos} ${group.totalProdutos === 1 ? "produto" : "produtos"}`
															: group.todasSemEstoque || !group.culturasSemEstoque?.length
																? "Sem estoque de nenhum produto"
																: `Sem estoque: ${group.culturasSemEstoque.join(", ")}`}
													</Typography>
												</Box>
											</Box>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
												{group.coletas.some((c) => coletasRetificadas.has(c.id)) && (
													<Box sx={{ px: 1.5, py: 0.25, borderRadius: "12px", bgcolor: "#f57c0015", color: "#f57c00", fontSize: "0.625rem", fontWeight: 600 }}>
														Retificação
													</Box>
												)}
												{group.temConfirmado && (
													<Box sx={{ px: 1.5, py: 0.25, borderRadius: "12px", bgcolor: "#27ae6015", color: "#27ae60", fontSize: "0.625rem", fontWeight: 500 }}>
														Confirmado
													</Box>
												)}
												{group.temPendente && (
													<Box sx={{ px: 1.5, py: 0.25, borderRadius: "12px", bgcolor: "#f5a62315", color: "#f5a623", fontSize: "0.625rem", fontWeight: 500 }}>
														Pendente
													</Box>
												)}
												{/* Mesmo chip da tela de WhatsApp: respondeu "nao vou ter" para o dia */}
												{group.semEstoque && (
													<Box sx={{ px: 1.5, py: 0.25, borderRadius: "12px", bgcolor: "#8e44ad15", color: "#8e44ad", fontSize: "0.625rem", fontWeight: 600 }}>
														Não terá estoque
													</Box>
												)}
												{expansivel && (isExpanded ? <ExpandLessIcon sx={{ color: "#8e9bae" }} /> : <ExpandMoreIcon sx={{ color: "#8e9bae" }} />)}
											</Box>
										</Box>
										<Collapse in={isExpanded}>
											<Box sx={{ bgcolor: "#f8f9fa" }}>
												{group.coletas.map((c) => (
													<Box key={c.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 4, py: 1.5, borderBottom: "1px solid #f0f2f5" }}>
														<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
															<LocalFloristIcon sx={{ fontSize: 14, color: "#8e9bae" }} />
															<Box>
																<Typography variant="body2" sx={{ fontWeight: 500, color: "#0f4557" }}>
																	{formatCultivo(c.produtoUnidadeDistribuidora?.cultivo)}
																</Typography>
																<Typography variant="caption" sx={{ color: "#8e9bae" }}>
																	{c.quantidade} {c.unidadeMedida?.sigla || ""}
																	{/* Pedido do WhatsApp que a coleta responde: duas carradas do
																	    mesmo produto no dia sao duas linhas. */}
																	{nomePedido(c) ? ` · ${nomePedido(c)}` : ""}
																</Typography>
															</Box>
														</Box>
														<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
															{coletasRetificadas.has(c.id) && (
																<Box sx={{ px: 1.5, py: 0.25, borderRadius: "12px", bgcolor: "#f57c0015", color: "#f57c00", fontSize: "0.625rem", fontWeight: 600 }}>
																	Retificada
																</Box>
															)}
															<Box sx={{
																px: 1.5,
																py: 0.25,
																borderRadius: "12px",
																bgcolor: c.status === "CONFIRMADO" ? "#27ae6015" : c.status === "CANCELADO" ? "#e74c3c15" : "#f5a62315",
																color: c.status === "CONFIRMADO" ? "#27ae60" : c.status === "CANCELADO" ? "#e74c3c" : "#f5a623",
																fontSize: "0.625rem",
																fontWeight: 500,
															}}>
																{c.status === "CONFIRMADO" ? "Confirmado" : c.status === "CANCELADO" ? "Cancelado" : "Pendente"}
															</Box>
															{c.status === "PENDENTE" && (
																<Button size="small" onClick={() => handleConfirmarColeta(c.id)} sx={{ fontSize: "0.625rem", minWidth: "auto", color: "#1a9e8f" }}>
																	Confirmar
																</Button>
															)}
															<IconButton size="small" onClick={() => openEditColeta(c)}>
																<EditIcon fontSize="small" />
															</IconButton>
															<IconButton size="small" onClick={() => { setDeleteTarget({ type: "coleta", id: c.id }); setDeleteDialogOpen(true); }}>
																<DeleteIcon fontSize="small" />
															</IconButton>
														</Box>
													</Box>
												))}
											</Box>
										</Collapse>
									</Box>
								);
							})
						)}
					</Card>
			</Box>

			{/* Demanda Dialog */}
			<Dialog open={demandaDialogOpen} onClose={() => setDemandaDialogOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>{editingDemanda ? "Editar Demanda" : "Cadastrar Demanda"}</DialogTitle>
				<DialogContent>
					<Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
						<FormControl fullWidth>
							<InputLabel>Produto</InputLabel>
							<Select value={formProduto} onChange={(e) => { setFormProduto(e.target.value); setFormUnidadeMedida(""); }} label="Produto">
								{produtos.map((p) => (
									<MenuItem key={p.id} value={p.id}>
										{formatCultivo(p.cultivo)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Box sx={{ display: "flex", gap: 2 }}>
							<TextField label="Quantidade" type="number" value={formQuantidade} onChange={(e) => setFormQuantidade(e.target.value)} sx={{ width: 130, flexShrink: 0 }} />
							<FormControl sx={{ flex: 1, minWidth: 0 }}>
								<InputLabel>Unidade</InputLabel>
								<Select value={formUnidadeMedida} onChange={(e) => setFormUnidadeMedida(e.target.value)} label="Unidade">
									{(() => {
										const produtoSelecionado = produtos.find((p) => p.id === formProduto);
										const embalagens = produtoSelecionado?.embalagens || [];
										const unidadesMap = new Map();
										embalagens.forEach((emb) => {
											if (emb.unidadeMedida && !unidadesMap.has(emb.unidadeMedida.id)) {
												unidadesMap.set(emb.unidadeMedida.id, emb.unidadeMedida);
											}
										});
										const unidadesFiltradas = Array.from(unidadesMap.values());
										if (unidadesFiltradas.length === 0) {
											return unidadesMedida.map((u) => (
												<MenuItem key={u.id} value={u.id}>{u.sigla}</MenuItem>
											));
										}
										return unidadesFiltradas.map((u) => (
											<MenuItem key={u.id} value={u.id}>{u.sigla}</MenuItem>
										));
									})()}
								</Select>
							</FormControl>
						</Box>
						<Typography variant="caption" sx={{ color: "#8e9bae" }}>
							Data: <strong>{selectedDate.toLocaleDateString("pt-BR")}</strong>
						</Typography>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDemandaDialogOpen(false)}>Cancelar</Button>
					<Button variant="contained" onClick={handleSaveDemanda} sx={{ bgcolor: "#1a9e8f" }}>
						{editingDemanda ? "Salvar Alterações" : "Cadastrar"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Coleta Dialog */}
			<Dialog open={coletaDialogOpen} onClose={() => setColetaDialogOpen(false)} maxWidth="xs" fullWidth>
				<DialogTitle>{editingColeta ? "Editar Coleta" : "Registrar Coleta"}</DialogTitle>
				<DialogContent>
					<Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
						<FormControl fullWidth>
							<InputLabel>Fornecedor</InputLabel>
							<Select value={formFornecedor} onChange={(e) => setFormFornecedor(e.target.value)} label="Fornecedor">
								<MenuItem value="">Sem fornecedor</MenuItem>
								{fornecedores.map((f) => (
									<MenuItem key={f.id} value={f.id}>
										{f.nome}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl fullWidth>
							<InputLabel>Produto</InputLabel>
							<Select value={formProduto} onChange={(e) => { setFormProduto(e.target.value); setFormUnidadeMedida(""); }} label="Produto">
								{produtos.map((p) => (
									<MenuItem key={p.id} value={p.id}>
										{formatCultivo(p.cultivo)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Box sx={{ display: "flex", gap: 2 }}>
							<TextField label="Quantidade" type="number" value={formQuantidade} onChange={(e) => setFormQuantidade(e.target.value)} sx={{ width: 130, flexShrink: 0 }} />
							<FormControl sx={{ flex: 1, minWidth: 0 }}>
								<InputLabel>Unidade</InputLabel>
								<Select value={formUnidadeMedida} onChange={(e) => setFormUnidadeMedida(e.target.value)} label="Unidade">
									{(() => {
										const produtoSelecionado = produtos.find((p) => p.id === formProduto);
										const embalagens = produtoSelecionado?.embalagens || [];
										const unidadesMap = new Map();
										embalagens.forEach((emb) => {
											if (emb.unidadeMedida && !unidadesMap.has(emb.unidadeMedida.id)) {
												unidadesMap.set(emb.unidadeMedida.id, emb.unidadeMedida);
											}
										});
										const unidadesFiltradas = Array.from(unidadesMap.values());
										if (unidadesFiltradas.length === 0) {
											return unidadesMedida.map((u) => (
												<MenuItem key={u.id} value={u.id}>{u.sigla}</MenuItem>
											));
										}
										return unidadesFiltradas.map((u) => (
											<MenuItem key={u.id} value={u.id}>{u.sigla}</MenuItem>
										));
									})()}
								</Select>
							</FormControl>
						</Box>
						<TextField label="Data Prevista" type="date" value={formDataPrevista} onChange={(e) => setFormDataPrevista(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setColetaDialogOpen(false)}>Cancelar</Button>
					<Button variant="contained" onClick={handleSaveColeta} sx={{ bgcolor: "#1a9e8f" }}>
						{editingColeta ? "Salvar Alterações" : "Registrar"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
				<DialogTitle>Confirmar exclusão</DialogTitle>
				<DialogContent>
					<Typography>
						Tem certeza que deseja excluir esta {deleteTarget?.type === "demanda" ? "demanda" : "coleta"}?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
					<Button variant="contained" color="error" onClick={handleDelete}>
						Excluir
					</Button>
				</DialogActions>
			</Dialog>

			{/* Snackbar */}
			<Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
				<Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</AppLayout>
	);
}

export default Coletas;
